import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { useCreateSkill } from "../../hooks/skills/useCreateSkill";
import { useUpdateSkill } from "../../hooks/skills/useUpdateSkill";
import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";

const FormAddEditSkill = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");

    // Form States
    const [nameAr, setNameAr] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [bioAr, setBioAr] = useState("");
    const [bioEn, setBioEn] = useState("");
    const [descriptionAr, setDescriptionAr] = useState("");
    const [descriptionEn, setDescriptionEn] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [images, setImages] = useState([]);

    // Hooks
    const { createSkillMutate, isPending: isCreating } = useCreateSkill();
    const { updateSkillMutate, isPending: isUpdating } = useUpdateSkill();

    const loadingSubmit = isCreating || isUpdating;

    // Fill form data in edit mode
    useEffect(() => {
        if (isEdit && currentData) {
            setNameAr(currentData?.name?.ar || "");
            setNameEn(currentData?.name?.en || "");
            setBioAr(currentData?.bio?.ar || "");
            setBioEn(currentData?.bio?.en || "");
            setDescriptionAr(currentData?.description?.ar || "");
            setDescriptionEn(currentData?.description?.en || "");
            setIsActive(currentData?.is_active ?? true);
            setImages(currentData?.images || []);
        }
    }, [isEdit, currentData]);

    const handleSubmit = (e) => {
        e.preventDefault();

        const formData = new FormData();
        formData.append('name[ar]', nameAr || nameEn);
        formData.append('name[en]', nameEn || nameAr);
        formData.append('bio[ar]', bioAr || bioEn);
        formData.append('bio[en]', bioEn || bioAr);
        formData.append('description[ar]', descriptionAr || descriptionEn);
        formData.append('description[en]', descriptionEn || descriptionAr);
        formData.append('is_active', isActive ? '1' : '0');

        // إضافة الصور
        images.forEach((image, index) => {
            if (image.file) {
                // صور جديدة يتم رفعها (إذا كان الحقل يدعم رفع ملفات مباشرة)
                // ملاحظة: المكون الحالي يرفع الصور فوراً ويضعها في images ككائنات
                formData.append(`images[${index}]`, image.file);
            } else {
                // صور موجودة مسبقاً أو تم رفعها بالفعل عبر المكون
                const imgValue = image.file_name || image.filename || (typeof image === 'string' ? image : null);
                if (imgValue) {
                    formData.append(`images[${index}]`, imgValue);
                }
            }
        });

        if (isEdit) {
            updateSkillMutate({ id: currentData.id, data: formData });
        } else {
            createSkillMutate(formData);
        }

        if (onSubmitSuccess) onSubmitSuccess();
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isEdit ? (isArabic ? "تعديل المهارة" : "Edit Skill") : (isArabic ? "إضافة مهارة جديدة" : "Add New Skill")}
                    </Typography>

                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    {/* Multilingual Names */}
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="اسم المهارة (عربي)"
                                placeholder="أدخل اسم المهارة بالعربي"
                                value={nameAr}
                                onChange={(e) => setNameAr(e.target.value)}
                                required
                                fullWidth
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Skill Name (English)"
                                placeholder="Enter skill name in English"
                                value={nameEn}
                                onChange={(e) => setNameEn(e.target.value)}
                                required
                                fullWidth
                            />
                        </Grid>
                    )}

                    {/* Multilingual Bio */}
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="السيرة الذاتية (عربي)"
                                placeholder="أدخل السيرة الذاتية بالعربي"
                                value={bioAr}
                                onChange={(e) => setBioAr(e.target.value)}
                                fullWidth
                                inputProps={{ maxLength: 255 }}
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Bio (English)"
                                placeholder="Enter bio in English"
                                value={bioEn}
                                onChange={(e) => setBioEn(e.target.value)}
                                fullWidth
                                inputProps={{ maxLength: 255 }}
                            />
                        </Grid>
                    )}

                    {/* Multilingual Description */}
                    {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <FormInput
                                label="وصف المهارة (عربي)"
                                placeholder="أدخل وصف المهارة بالعربي"
                                value={descriptionAr}
                                onChange={(e) => setDescriptionAr(e.target.value)}
                                multiline
                                rows={4}
                                fullWidth
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <FormInput
                                label="Skill Description (English)"
                                placeholder="Enter skill description in English"
                                value={descriptionEn}
                                onChange={(e) => setDescriptionEn(e.target.value)}
                                multiline
                                rows={4}
                                fullWidth
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "نشط" : "Active"}
                            type="switch"
                            name="is_active"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormInput
                            label={isArabic ? "صور المهارة" : "Skill Images"}
                            type="multi-image"
                            name="images"
                            value={images}
                            onChange={setImages}
                            maxImages={10}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loadingSubmit}
                        sx={{ minWidth: 120 }}
                    >
                        {loadingSubmit ? (isArabic ? "جاري الحفظ..." : "Saving...") : (isArabic ? "حفظ" : "Save")}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default FormAddEditSkill;